import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

// Grows a textarea's height to fit its content as the user types, instead of
// requiring a manual drag-resize on the corner handle or showing an internal
// y-scrollbar. Attach `ref` to the textarea - resizing re-runs automatically
// whenever `value` changes. useLayoutEffect (not useEffect) runs synchronously
// before paint, so a controlled-input keystroke is measured and resized in the
// same frame, with no separate onInput call needed.
const useAutoResizeTextarea = (value: string) => {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // Temporarily set rows=1 so the HTML rows attribute doesn't act as a
    // minimum-height floor when we reset height to 'auto'. Without this,
    // a textarea with rows={4} or rows={6} keeps a large blank gap below
    // short content even after the auto-resize runs.
    const savedRows = el.rows;
    el.rows = 1;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    el.rows = savedRows;
  }, []);

  useLayoutEffect(() => {
    resize();
  }, [value, resize]);

  // Re-measure once webfonts finish loading - if a measurement happens before
  // that, scrollHeight is computed against fallback font metrics and is left
  // stale (too tall). document.fonts.ready resolves once, so this only needs
  // to subscribe on mount, not on every value change.
  useEffect(() => {
    if (!document.fonts) return;
    document.fonts.ready.then(resize).catch(() => {});
  }, [resize]);

  return { ref, resize };
};

export default useAutoResizeTextarea;
